import React from 'react';
import { ArrowPathIcon, ChevronRightIcon, ClockIcon } from '@heroicons/react/24/outline';

import { messageFromError, messageOfFailure } from '@oysterjs/core/graphql/errors';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm
} from '@oysterjs/ui/description-list';
import { MountableSlideout } from '@oysterjs/ui/slideout';
import { Spinner } from '@oysterjs/ui/spinner';

import { client } from '~/api/client';
import {
  type MailboxAccountFieldsFragment,
  MailboxEventsDocument,
  type MailboxEventsQuery,
  type MailboxEventsQueryVariables,
  MailboxEventType,
  MailboxFolderRole,
  UpdateInitiatorType,
  useGetAgencyUsersQuery
} from '~/api/graphql';
import { FormError } from '~/components/form-error';
import { rowDateTimeFormatter } from '~/components/table-formatters';

const eventLimit = 200;
const readFailure = 'We could not load mailbox events. Please try again.';
type MailboxEvent = MailboxEventsQuery['mailboxEvents'][number];
type MessageActivity = NonNullable<MailboxEventsQuery['mailboxMessageActivity']>[number];
type TimelineEntry = { id: string; occurredAt: Date } & (
  | { event: MailboxEvent; message?: never }
  | { event?: never; message: MessageActivity }
);

const eventPresentation: Record<
  MailboxEventType,
  { title: string; description: string; color: React.ComponentProps<typeof Badge>['color'] }
> = {
  [MailboxEventType.NotificationActivity]: {
    title: 'Notification activity',
    description: 'A mailbox notification was recorded.',
    color: 'zinc'
  },
  [MailboxEventType.Connected]: {
    title: 'Mailbox connected',
    description: 'This mailbox was connected to Cara.',
    color: 'green'
  },
  [MailboxEventType.Reconnected]: {
    title: 'Mailbox reconnected',
    description: 'The sign-in was renewed so Cara can use this mailbox again.',
    color: 'green'
  },
  [MailboxEventType.Disconnected]: {
    title: 'Mailbox disconnected',
    description: 'The mailbox connection and its credentials were removed from Cara.',
    color: 'zinc'
  },
  [MailboxEventType.ReauthRequired]: {
    title: 'Reconnect required',
    description: 'Cara could no longer use the stored sign-in for this mailbox.',
    color: 'amber'
  },
  [MailboxEventType.DraftCreated]: {
    title: 'Draft created',
    description: 'Cara saved an email as a draft in this mailbox.',
    color: 'sky'
  },
  [MailboxEventType.DraftSynced]: {
    title: 'Draft updated',
    description: 'Cara synced a newer version of an email to its mailbox draft.',
    color: 'sky'
  },
  [MailboxEventType.DraftRelocated]: {
    title: 'Draft moved',
    description: 'Cara updated the recorded location of a draft in this mailbox.',
    color: 'sky'
  },
  [MailboxEventType.DraftRetired]: {
    title: 'Draft retired',
    description: 'Cara stopped tracking this mailbox draft.',
    color: 'zinc'
  },
  [MailboxEventType.MonitoringStarted]: {
    title: 'Monitoring enabled',
    description: 'The owner asked Cara to monitor this mailbox.',
    color: 'green'
  },
  [MailboxEventType.MonitoringStopped]: {
    title: 'Monitoring disabled',
    description: 'The owner asked Cara to stop monitoring this mailbox.',
    color: 'zinc'
  },
  [MailboxEventType.MonitoringInterrupted]: {
    title: 'Monitoring interrupted',
    description: 'Notifications may have been missed. Cara will recheck its drafts.',
    color: 'amber'
  }
};

const initiatorLabels: Record<UpdateInitiatorType, string> = {
  [UpdateInitiatorType.AgencyClient]: 'Agency client',
  [UpdateInitiatorType.AgencyUser]: 'Agency user',
  [UpdateInitiatorType.Anonymous]: 'Anonymous',
  [UpdateInitiatorType.Cronjob]: 'Cara (automatic)',
  [UpdateInitiatorType.Oyster]: 'Oyster',
  [UpdateInitiatorType.Workflow]: 'Cara (automatic)'
};

const knownValue = <T,>(values: Record<string, T>, key: string): T | undefined =>
  Object.hasOwn(values, key) ? values[key] : undefined;

const reasonLabels: Record<string, string> = {
  grant_rejected: 'The provider rejected the sign-in',
  token_persist_failed: 'The renewed sign-in could not be saved',
  lapsed: 'The notification subscription expired',
  removed: 'The provider removed the notification subscription',
  missed: 'The provider reported missed notifications',
  sent_via_cara: 'Sent through Cara',
  sent_externally: 'Sent outside Cara',
  deleted_externally: 'Deleted in the mailbox',
  vanished: 'The draft could no longer be found',
  superseded: 'Replaced by another draft',
  retracted: 'Withdrawn from the mailbox'
};

const notificationTitles: Record<string, string> = {
  received: 'Notification received',
  dispatch_confirmed: 'Processing queued',
  dispatch_unconfirmed: 'Queue result unconfirmed',
  processing_started: 'Processing started',
  processed: 'Notification processed',
  ignored: 'Notification ignored',
  duplicate: 'Duplicate notification',
  failed: 'Processing failed'
};

const notificationReasons: Record<string, string> = {
  checked: 'Message checked',
  monitoring_disabled: 'Monitoring is disabled',
  change_type_disabled: 'This change type is disabled',
  untracked_message: 'Not a draft tracked by Cara',
  untracked_draft_or_change: 'No applicable change for a Cara draft',
  untracked_draft: 'A draft not tracked by Cara',
  coalesced_in_delivery: 'Combined with another change in this delivery',
  message_already_recorded: 'This message was already recorded',
  message_recorded: 'Message reference recorded',
  message_gone: 'Message no longer available',
  mailbox_disconnected: 'Mailbox was disconnected',
  processing_failed: 'Cara could not finish processing this notification',
  subscription_signal: 'Subscription signal handled',
  external_draft_edit_detected: 'External draft edit detected',
  draft_matches_baseline: 'Draft matches Cara’s last saved copy',
  stale_draft_comparison: 'A newer draft check or write superseded this check',
  draft_baseline_unavailable: 'No saved draft baseline to compare'
};

const notificationChanges: Record<string, string> = {
  added: 'Message added',
  updated: 'Message changed',
  deleted: 'Message deleted'
};
const notificationSignals: Record<string, string> = {
  missed: 'Missed notifications',
  removed: 'Subscription removed',
  renewal_due: 'Subscription renewal required'
};

const ActivityDetails = ({ children }: { children: React.ReactNode }) => (
  <details className="group mt-3">
    <summary className="flex w-fit cursor-pointer list-none items-center gap-1 rounded text-xs font-medium text-neutral-600 hover:text-neutral-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-500 dark:text-neutral-300 dark:hover:text-white [&::-webkit-details-marker]:hidden">
      <ChevronRightIcon
        aria-hidden="true"
        className="size-3 transition-transform group-open:rotate-90"
      />
      Event details
    </summary>
    <DescriptionList className="mt-2 [&_dd]:min-w-0 [&_dd]:break-words [&_dd]:text-xs [&_dt]:text-xs">
      {children}
    </DescriptionList>
  </details>
);

const EventDetails = ({ event }: { event: MailboxEvent }) => (
  <ActivityDetails>
    <DescriptionTerm>Occurred at (UTC)</DescriptionTerm>
    <DescriptionDetails>{new Date(event.occurredAt).toISOString()}</DescriptionDetails>
    {event.notification?.folderName && (
      <>
        <DescriptionTerm>Historical folder</DescriptionTerm>
        <DescriptionDetails>{event.notification.folderName}</DescriptionDetails>
      </>
    )}
    {event.artifactVersion != null && (
      <>
        <DescriptionTerm>Email version</DescriptionTerm>
        <DescriptionDetails>{event.artifactVersion}</DescriptionDetails>
      </>
    )}
  </ActivityDetails>
);

const folderLabels: Record<MailboxFolderRole, string> = {
  [MailboxFolderRole.Inbox]: 'Inbox',
  [MailboxFolderRole.Sent]: 'Sent Items',
  [MailboxFolderRole.Drafts]: 'Drafts',
  [MailboxFolderRole.Archive]: 'Archive',
  [MailboxFolderRole.Junk]: 'Junk',
  [MailboxFolderRole.Trash]: 'Trash'
};

const MessageEntry = ({ message }: { message: MessageActivity }) => {
  // The saved name is historical; the role is today's mapping. Never combine
  // them into a claim about the message's historical direction or delivery.
  const title = 'Message observed';
  const folder = message.folderName;
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Badge color={message.folderRole === MailboxFolderRole.Sent ? 'violet' : 'sky'}>
          {title}
        </Badge>
        <time
          dateTime={new Date(message.recordedAt).toISOString()}
          className="text-xs text-neutral-500 dark:text-neutral-400"
        >
          {rowDateTimeFormatter.format(new Date(message.recordedAt))}
        </time>
      </div>
      <p className="mt-1 break-words text-xs text-neutral-500 dark:text-neutral-400">
        Recorded by Cara{folder ? ` · ${folder}` : ' · Watched folder'}
      </p>
      <ActivityDetails>
        <DescriptionTerm>Recorded at (UTC)</DescriptionTerm>
        <DescriptionDetails>{new Date(message.recordedAt).toISOString()}</DescriptionDetails>
        <DescriptionTerm>Historical folder</DescriptionTerm>
        <DescriptionDetails>{folder || 'Historical folder name unavailable'}</DescriptionDetails>
        {message.folderRole && (
          <>
            <DescriptionTerm>Current folder mapping</DescriptionTerm>
            <DescriptionDetails>
              {knownValue(folderLabels, message.folderRole) ?? 'Unavailable'}
            </DescriptionDetails>
          </>
        )}
      </ActivityDetails>
    </>
  );
};

export const MailboxEventsDrawer = ({
  account,
  includeMessages,
  ownershipKnown = true,
  onClose
}: {
  account: Pick<MailboxAccountFieldsFragment, 'id' | 'identity'>;
  includeMessages: boolean;
  ownershipKnown?: boolean;
  onClose: () => void;
}) => {
  const [activity, setActivity] = React.useState(false);
  const activitySelected = includeMessages && activity;
  const [entries, setEntries] = React.useState<TimelineEntry[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [refresh, setRefresh] = React.useState(0);
  const { data: directory } = useGetAgencyUsersQuery();

  React.useEffect(() => {
    let current = true;
    setEntries(null);
    setError(null);
    void client
      .query<MailboxEventsQuery, MailboxEventsQueryVariables>({
        query: MailboxEventsDocument,
        variables: {
          mailboxAccountId: account.id,
          limit: eventLimit,
          includeMessages: activitySelected
        },
        fetchPolicy: 'network-only'
      })
      .then((result) => {
        if (!current) return;
        // A partial result under errorPolicy: 'all' is not a complete audit trail.
        const failure = messageOfFailure(result, readFailure);
        if (
          failure ||
          !result.data?.mailboxEvents ||
          (activitySelected && !result.data.mailboxMessageActivity)
        ) {
          setError(failure ?? readFailure);
          return;
        }
        const entries: TimelineEntry[] = [
          ...result.data.mailboxEvents.map((event) => ({
            id: event.id,
            occurredAt: new Date(event.occurredAt),
            event
          })),
          ...(activitySelected ? (result.data.mailboxMessageActivity ?? []) : []).map(
            (message) => ({
              id: message.id,
              occurredAt: new Date(message.recordedAt),
              message
            })
          )
        ];
        // Lifecycle is fetched separately, so private traffic cannot consume its cap.
        entries.sort(
          (a, b) =>
            b.occurredAt.getTime() - a.occurredAt.getTime() ||
            (a.id === b.id ? 0 : a.id < b.id ? 1 : -1)
        );
        setEntries(entries.slice(0, eventLimit));
      })
      .catch((err: unknown) => {
        if (current) setError(messageFromError(err, readFailure));
      });
    return () => {
      current = false;
    };
  }, [account.id, activitySelected, refresh]);

  const userLabel = (id: string) => {
    const user = directory?.agencyUsers.find((person) => person.id === id);
    return user?.name || user?.email || initiatorLabels[UpdateInitiatorType.AgencyUser];
  };
  const initiatorLabel = (event: MailboxEvent) =>
    event.initiatorType === UpdateInitiatorType.AgencyUser
      ? userLabel(event.initiatorId)
      : (knownValue(initiatorLabels, event.initiatorType) ?? 'Initiator unavailable');
  const loading = entries === null && !error;
  // Ownership may change before the next fetch settles. Never flash private
  // rows from the previous result while rendering a non-owner view.
  const visibleEntries = entries?.filter(
    (entry) =>
      includeMessages ||
      (!entry.message && entry.event.type !== MailboxEventType.NotificationActivity)
  );

  return (
    <MountableSlideout
      title="Mailbox events"
      size="xl"
      onClose={onClose}
      actions={
        <Button
          outline
          loading={loading}
          disabled={loading}
          onClick={() => setRefresh((value) => value + 1)}
        >
          <ArrowPathIcon data-slot="icon" />
          Refresh
        </Button>
      }
    >
      <div className="mb-6">
        <p className="break-words text-sm font-medium text-neutral-900 dark:text-white">
          {account.identity.email}
        </p>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          {includeMessages
            ? 'Connection, monitoring, draft, and message-reference activity. Ordinary email subjects, senders, recipients, bodies and attachments are not included.'
            : ownershipKnown
              ? 'Connection, monitoring, and draft activity. Message activity is visible only to the mailbox owner.'
              : 'Connection, monitoring, and draft activity. Ownership is not confirmed yet; private activity is unavailable.'}
        </p>
        {includeMessages && (
          <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
            Message references appear on first observation in a watched folder. Notification entries
            distinguish receipt, queueing, processing and outcomes. An open attempt does not prove
            completion. This is not a complete mailbox history, current message state, or proof of
            delivery.
          </p>
        )}
      </div>

      {includeMessages && (
        <div aria-label="Activity streams" className="mb-4 flex gap-2">
          <Button outline aria-pressed={!activitySelected} onClick={() => setActivity(false)}>
            Lifecycle
          </Button>
          <Button outline aria-pressed={activitySelected} onClick={() => setActivity(true)}>
            Message and notification activity
          </Button>
        </div>
      )}

      {loading ? (
        <div
          role="status"
          className="flex items-center justify-center gap-2 py-12 text-sm text-neutral-500 dark:text-neutral-400"
        >
          <Spinner size="size-6" color="neutral" />
          Loading mailbox events…
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-500/20 dark:bg-red-500/10">
          <FormError message={error} className="text-sm text-red-700 dark:text-red-400" />
          <Button outline className="mt-3" onClick={() => setRefresh((value) => value + 1)}>
            Try again
          </Button>
        </div>
      ) : visibleEntries?.length === 0 ? (
        <div className="py-12 text-center">
          <ClockIcon
            aria-hidden="true"
            className="mx-auto size-8 text-neutral-400 dark:text-neutral-500"
          />
          <h3 className="mt-3 text-sm font-medium text-neutral-900 dark:text-white">
            {activitySelected
              ? 'No message or notification activity yet'
              : 'No lifecycle events yet'}
          </h3>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            {activitySelected
              ? 'Recorded message references and notifications will appear here.'
              : 'Recorded connection, monitoring, and draft activity will appear here.'}
          </p>
        </div>
      ) : (
        <>
          <p className="mb-4 text-xs text-neutral-500 dark:text-neutral-400">
            Newest recorded first · Times shown in your local timezone
          </p>
          <ol
            aria-label="Mailbox event timeline"
            className="space-y-6 border-l border-neutral-200 pl-5 dark:border-neutral-700"
          >
            {visibleEntries?.map((entry) => {
              if (entry.message)
                return (
                  <li key={entry.id} className="relative min-w-0">
                    <span
                      aria-hidden="true"
                      className="absolute -left-[1.5625rem] top-2 size-2 rounded-full bg-neutral-300 dark:bg-neutral-600"
                    />
                    <MessageEntry message={entry.message} />
                  </li>
                );
              const event = includeMessages ? entry.event : { ...entry.event, notification: null };
              const notification =
                event.type === MailboxEventType.NotificationActivity ? event.notification : null;
              const presentation = knownValue(eventPresentation, event.type) ?? {
                title: 'Mailbox activity recorded',
                description: 'Mailbox activity recorded.',
                color: 'zinc' as const
              };
              return (
                <li key={event.id} className="relative min-w-0">
                  <span
                    aria-hidden="true"
                    className="absolute -left-[1.5625rem] top-2 size-2 rounded-full bg-neutral-300 dark:bg-neutral-600"
                  />
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <Badge color={notification?.outcome === 'failed' ? 'red' : presentation.color}>
                      {notification
                        ? (knownValue(notificationTitles, notification.outcome) ??
                          'Notification activity')
                        : presentation.title}
                    </Badge>
                    <time
                      dateTime={new Date(event.occurredAt).toISOString()}
                      className="text-xs text-neutral-500 dark:text-neutral-400"
                    >
                      {rowDateTimeFormatter.format(new Date(event.occurredAt))}
                    </time>
                  </div>
                  <p className="mt-2 text-sm text-neutral-700 dark:text-neutral-200">
                    {notification
                      ? [
                          [
                            notification.changeType &&
                              `Message change: ${knownValue(notificationChanges, notification.changeType) ?? 'Unknown change'}`,
                            notification.watchSignal &&
                              `Subscription signal: ${knownValue(notificationSignals, notification.watchSignal) ?? 'Unknown signal'}`
                          ]
                            .filter(Boolean)
                            .join(' · ') || 'Mailbox notification',
                          notification.folderName
                        ]
                          .filter(Boolean)
                          .join(' · ')
                      : presentation.description}
                  </p>
                  {notification?.reason && (
                    <p className="mt-1 break-words text-sm text-neutral-700 dark:text-neutral-200">
                      {knownValue(notificationReasons, notification.reason) ??
                        'Processing detail unavailable'}
                    </p>
                  )}
                  {event.reason && (
                    <p className="mt-1 break-words text-sm text-neutral-700 dark:text-neutral-200">
                      {knownValue(reasonLabels, event.reason) ?? 'Reason unavailable'}
                    </p>
                  )}
                  <p className="mt-1 break-words text-xs text-neutral-500 dark:text-neutral-400">
                    {initiatorLabel(event)}
                    {event.requestedByUserId
                      ? ` · Requested by ${userLabel(event.requestedByUserId)}`
                      : ''}
                  </p>
                  <EventDetails event={event} />
                </li>
              );
            })}
          </ol>
          {visibleEntries?.length === eventLimit && (
            <p className="mt-6 text-xs text-neutral-500 dark:text-neutral-400">
              Showing the latest {eventLimit} events. Older events are not shown.
            </p>
          )}
        </>
      )}
    </MountableSlideout>
  );
};
